import React, { useCallback } from 'react';

import { updateBalance } from 'common/api/wallet/cards';
import { useApi } from 'common/modules/async-actions/useApi';
import { Form, Formik } from 'formik';
import { Btn } from 'components/btn/Btn';
import { monetizeWithDecimal } from 'common/utils/strings';
import { Modal, ModalSize } from 'components/modal/Modal';
import { FormikTextField } from 'features/product/components/utils/fields';
import { CARD_AMOUNT_MAX } from 'common/api/config';
import { useAlert } from 'modules/alerts';
import { defaultApiErrorMessage } from 'utils/apiResponseMessages';
import { isEmpty } from 'common/modules/form-validation';
import { LinkBtn } from 'components/link-btn';

export const ModalTrackDialog = ({
    cardId,
    currentBalance,
    isOpen,
    setIsOpen,
    onBalanceUpdated,
}: {
    cardId: string;
    currentBalance: number;
    isOpen: boolean;
    setIsOpen: (value: boolean) => unknown;
    onBalanceUpdated?: (value: number) => unknown;
}) => (
    <>
        <Modal
            title={'Track Balance'}
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            size={[ModalSize.CONTENT_WIDTH, ModalSize.CONTENT_WIDTH]}
        >
            <TrackDialog
                cardId={cardId}
                currentBalance={currentBalance}
                setIsOpen={setIsOpen}
                onBalanceUpdated={onBalanceUpdated}
            />
        </Modal>
    </>
);

interface FormValues {
    value?: string;
}

const TrackDialog = ({
    cardId,
    currentBalance,
    setIsOpen,
    onBalanceUpdated,
}: {
    cardId: string;
    currentBalance: number;
    setIsOpen: (value: boolean) => unknown;
    onBalanceUpdated?: (value: number) => unknown;
}) => {
    const [doUpdateBalance, doUpdateBalanceAsyncState] = useApi(updateBalance);

    const validate = useCallback(({ value }: FormValues) => {
        const valueStr = (value ?? '').toString();
        const valueNumeric = Number(value);

        if (isEmpty(valueStr) || valueStr.endsWith('.')) {
            return { value: 'Invalid value' };
        }

        if (valueNumeric < 0 || valueNumeric > CARD_AMOUNT_MAX) {
            return {
                value: `Card balance must be between 0 and ${CARD_AMOUNT_MAX}.`,
            };
        }
    }, []);

    const alert = useAlert();

    const onSubmit = useCallback(
        async ({ value }: FormValues) => {
            const valueNumeric = Number(value);
            const response = await doUpdateBalance(cardId, valueNumeric || 0);

            if (response.error) {
                alert({
                    title: 'Error updating balance',
                    message: defaultApiErrorMessage().message,
                });
                return;
            }

            setIsOpen(false);

            if (onBalanceUpdated) {
                onBalanceUpdated(valueNumeric || 0);
            }
        },
        [alert, cardId, doUpdateBalance, onBalanceUpdated, setIsOpen]
    );

    return (
        <Formik<FormValues>
            initialValues={{ value: undefined }}
            validate={validate}
            onSubmit={onSubmit}
        >
            {(formikProps) => (
                <Form>
                    <div className="text-grey-1 text-base">
                        This card requires that you manually track your balance. Your Balance:{' '}
                        {monetizeWithDecimal(currentBalance)}
                    </div>
                    <div className="mt-6">
                        <FormikTextField
                            label="New Card Balance"
                            name="value"
                            placeholder="0.00"
                            type="number"
                            style={{ textAlign: 'center' }}
                            step="0.01"
                            autoComplete="off"
                        />
                        <div className="w-full block mt-12">
                            <div className="flex items-center justify-center">
                                <Btn
                                    type="submit"
                                    disabled={
                                        doUpdateBalanceAsyncState.loading ||
                                        Boolean(formikProps.errors.value)
                                    }
                                >
                                    Update Balance
                                </Btn>
                            </div>
                            <div className="text-center mt-4">
                                <LinkBtn className="text-brand" onClick={() => setIsOpen(false)}>
                                    Cancel
                                </LinkBtn>
                            </div>
                        </div>
                    </div>
                </Form>
            )}
        </Formik>
    );
};
